import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Card,
  Modal,
  Form,
  Input,
  Typography,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { attributeService } from '../../services';

const { Title } = Typography;

const AttributeValueManager = () => {
  const [values, setValues] = useState([]);
  const [attribute, setAttribute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    loadAttribute();
  }, [id]);

  const loadAttribute = async () => {
    try {
      const response = await attributeService.getById(id);
      if (response.success && response.data) {
        setAttribute(response.data);
        // Load values from the attribute data if available
        if (response.data.values) {
          setValues(response.data.values);
        } else {
          setValues([]);
        }
      } else {
        message.error('Không thể tải thông tin thuộc tính');
        navigate('/attributes');
      }
    } catch (error) {
      //console.error('Error loading attribute:', error);
      message.error('Không thể tải thông tin thuộc tính');
      navigate('/attributes');
    }
  };

  const handleAdd = () => {
    setEditingValue(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingValue(record);
    form.setFieldsValue({ value: record.value });
    setModalVisible(true);
  };

  const handleDelete = async (valueId) => {
    try {
      const response = await attributeService.deleteValue(valueId);
      if (response.success) {
        message.success('Xóa giá trị thành công');
        loadAttribute(); // Reload to get updated data
      } else {
        message.error(response.message || 'Xóa giá trị thất bại');
      }
    } catch (error) {
      //console.error('Error deleting value:', error);
      const errorMsg = error.response?.data?.message || 'Lỗi khi xóa giá trị';
      message.error(errorMsg);
    }
  };

  const handleSubmit = async (formValues) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      message.error('Vui lòng đăng nhập lại');
      return;
    }

    try {
      const data = { ...formValues, accessToken };
      let response;

      if (editingValue) {
        // Gửi đúng dạng: chỉ truyền chuỗi value, không truyền cả object chứa accessToken
        response = await attributeService.updateValue(editingValue.id, formValues.value);
      } else {
        data.attribute_id = parseInt(id);
        response = await attributeService.addValue(id, formValues.value);
      }

      if (response.success) {
        message.success(editingValue ? 'Cập nhật giá trị thành công' : 'Thêm giá trị thành công');
        setModalVisible(false);
        loadAttribute(); // Reload to get updated data
      } else {
        message.error(response.message || 'Lưu giá trị thất bại');
      }
    } catch (error) {
      //console.error('Error saving value:', error);
      const errorMsg = error.response?.data?.message || 'Lỗi khi lưu giá trị';
      message.error(errorMsg);
    }
  };

  // Filter values based on search
  const filteredValues = values.filter(value =>
    value.value?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Giá trị',
      dataIndex: 'value',
      key: 'value',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Sửa
          </Button>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa giá trị này?"
            description="Hành động này có thể ảnh hưởng đến các sản phẩm đang sử dụng giá trị này."
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Xóa
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/attributes')}
            >
              Quay lại
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              Quản lý giá trị: {attribute?.name}
            </Title>
          </Space>
        </div>

        {/* Header và bộ lọc */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Tìm kiếm giá trị..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadAttribute}
                loading={loading}
              >
                Làm mới
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Thêm giá trị
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Thống kê */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <div style={{ 
              background: '#f0f9ff', 
              padding: 16, 
              borderRadius: 8,
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {filteredValues.length}
              </div>
              <div style={{ color: '#666' }}>Tổng giá trị</div>
            </div>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredValues}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} giá trị`,
          }}
        />

        <Modal
          title={editingValue ? 'Sửa giá trị' : 'Thêm giá trị'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          okText={editingValue ? 'Cập nhật' : 'Thêm'}
          cancelText="Hủy"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Giá trị"
              name="value"
              rules={[
                { required: true, message: 'Vui lòng nhập giá trị' },
                { min: 1, message: 'Giá trị không được để trống' },
                { max: 255, message: 'Giá trị không được quá 255 ký tự' }
              ]}
            >
              <Input placeholder="Nhập giá trị thuộc tính" />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default AttributeValueManager;
